import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMembership } from "@/context/MembershipContext";
import { User, CreditCard, CalendarBlank, ShieldCheck, SignOut, Warning, X } from "@phosphor-icons/react";
import CancelSubscriptionModal from "@/components/CancelSubscriptionModal";
import NotificationSettings from "@/components/NotificationSettings";

/**
 * /app/account — client account & subscription hub.
 *
 * Surfaces:
 *   - Name / email
 *   - Current membership state (plan · next bill · days remaining · cancel-at-period-end)
 *   - Cancel-subscription button (opens CancelSubscriptionModal → POST /payments/cancel-subscription)
 *   - Notification preferences (existing NotificationSettings component)
 *   - Sign out
 *
 * Membership snapshot comes from GET /payments/me (same endpoint the paywall uses).
 * After a successful cancellation, the membership context refreshes so the badge
 * flips from "Renews DD MMM" to "Cancelled · access until DD MMM".
 */
export default function ClientAccountPage() {
  const { user, logout } = useAuth();
  const { membership, refresh: refreshMembership } = useMembership();
  const nav = useNavigate();

  const [status, setStatus] = useState(null);
  const [err, setErr] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await http.get("/payments/me");
      setStatus(data);
    } catch (e) { setErr(apiError(e, "Couldn't load your subscription details.")); }
  };
  useEffect(() => { load(); }, []);

  const handleLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  const handleCancelled = async () => {
    setCancelOpen(false);
    await load();                // refresh local view
    if (refreshMembership) await refreshMembership();  // refresh global paywall badge
  };

  const active = !!status?.active;
  const cancelling = !!status?.cancel_at_period_end;
  const hasSubscription = !!status?.has_subscription;
  const isCoachRow = user?.role === "coach";

  const nextBillISO = status?.renews_at;
  const accessUntilISO = status?.expires_at;
  const fmtDate = (iso) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
    catch { return null; }
  };

  return (
    <div className="p-6 md:p-10 max-w-3xl mx-auto" data-testid="client-account-page">
      <div className="id-label mb-3">— Account —</div>
      <h1 className="id-h1 mb-8">
        Your <span className="text-[#FF3333]">Membership</span>
      </h1>

      {err && (
        <div className="border border-[#FF3333] bg-[#FF3333]/10 px-4 py-3 text-sm text-[#FF3333] font-manrope mb-6" data-testid="account-error">
          {err}
        </div>
      )}

      {/* --- Profile --- */}
      <div className="id-card mb-6" data-testid="account-profile-card">
        <div className="id-label mb-3 flex items-center gap-2"><User size={12} weight="fill" /> Profile</div>
        <div className="space-y-2 font-manrope text-sm">
          <div className="flex justify-between gap-3">
            <span className="text-zinc-500">Name</span>
            <span className="text-white text-right truncate" data-testid="account-name">{user?.name || "—"}</span>
          </div>
          <div className="flex justify-between gap-3">
            <span className="text-zinc-500">Email</span>
            <span className="text-white text-right truncate" data-testid="account-email">{user?.email || "—"}</span>
          </div>
          {user?.founder_number ? (
            <div className="flex justify-between gap-3">
              <span className="text-zinc-500">Founder number</span>
              <span className="text-[#FF3333] font-bold" data-testid="account-founder-number">#{user.founder_number} of 20</span>
            </div>
          ) : null}
        </div>
      </div>

      {/* --- Subscription --- */}
      <div className="id-card mb-6" data-testid="account-subscription-card">
        <div className="id-label mb-3 flex items-center gap-2"><CreditCard size={12} weight="fill" /> Subscription</div>

        {isCoachRow ? (
          <div className="text-zinc-400 text-sm font-manrope italic">Coaches don&apos;t need a membership.</div>
        ) : (
          <div className="space-y-3">
            {/* If not active + not cancelling, show a small paywall link but STILL
                render the Cancel button below (in case the local state is stale
                and the user genuinely has an active Stripe sub). */}
            {!active && !cancelling && (
              <div className="border border-zinc-700 bg-zinc-900/50 px-3 py-2 text-xs font-manrope text-zinc-400 flex items-center justify-between gap-3" data-testid="account-inactive-notice">
                <span>No active membership showing on file.</span>
                <button
                  onClick={() => nav("/app/subscribe")}
                  className="text-[#FF3333] uppercase tracking-[0.15em] font-bold whitespace-nowrap"
                  data-testid="account-subscribe-btn"
                >
                  Activate →
                </button>
              </div>
            )}

            {(active || cancelling || hasSubscription) && (
              <>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div>
                    <div className="font-oswald text-xl uppercase tracking-wide text-white">
                      {cancelling ? "Cancelled — winding down" : "Active"}
                    </div>
                    {status?.program_plan ? (
                      <div className="text-zinc-500 text-xs font-manrope mt-0.5">
                        {status.program_plan === "full" ? "Pay-in-Full · 12-week program" :
                         status.program_plan === "continued" ? "Continued coaching · $69/wk" :
                         "12-week payment plan"}
                      </div>
                    ) : null}
                  </div>
                  <div className={"text-[10px] font-manrope uppercase tracking-[0.2em] font-bold " + (cancelling ? "text-[#FF3333]" : "text-[#00CC66]")}>
                    {cancelling ? "Ending soon" : "In good standing"}
                  </div>
                </div>

                <div className="space-y-1.5 pt-3 border-t border-[#262626] font-manrope text-sm">
                  {!cancelling && nextBillISO && (
                    <div className="flex justify-between gap-3">
                      <span className="text-zinc-500 flex items-center gap-2"><CalendarBlank size={12} weight="fill" /> Next payment</span>
                      <span className="text-white" data-testid="account-next-bill">{fmtDate(nextBillISO)}</span>
                    </div>
                  )}
                  {accessUntilISO && (
                    <div className="flex justify-between gap-3">
                      <span className="text-zinc-500 flex items-center gap-2"><CalendarBlank size={12} weight="fill" /> Access until</span>
                      <span className="text-white" data-testid="account-access-until">{fmtDate(accessUntilISO)}</span>
                    </div>
                  )}
                  {typeof status?.days_remaining === "number" && status.days_remaining > 0 && (
                    <div className="flex justify-between gap-3">
                      <span className="text-zinc-500">Days remaining</span>
                      <span className="text-white" data-testid="account-days-remaining">{status.days_remaining}</span>
                    </div>
                  )}
                </div>
              </>
            )}

            {cancelling ? (
              <div className="mt-4 border-l-2 border-[#FF3333] bg-[#FF3333]/5 px-4 py-3 text-xs font-manrope text-zinc-300" data-testid="account-cancelled-notice">
                <div className="flex items-start gap-2">
                  <Warning size={14} weight="fill" className="text-[#FF3333] shrink-0 mt-0.5" />
                  <div>
                    <div className="text-[#FF3333] font-bold uppercase tracking-[0.15em] text-[10px] mb-1">Cancellation in effect</div>
                    You&apos;ll keep full access until <span className="text-white font-bold">{fmtDate(accessUntilISO) || "period end"}</span>.
                    No further charges. Message Jimmy in Coach Chat any time — happy to help you land.
                  </div>
                </div>
              </div>
            ) : (
              // Always show Cancel when not a coach and not already cancelling.
              // Don't gate on `active` — local state can be temporarily stale
              // during the Stripe-sync refresh, causing the button to flicker
              // in/out (bug reported in prod). Backend endpoint is defensive.
              <button
                onClick={() => setCancelOpen(true)}
                className="mt-4 text-xs font-manrope uppercase tracking-[0.15em] text-zinc-500 hover:text-[#FF3333] underline"
                data-testid="account-cancel-btn"
              >
                Cancel subscription
              </button>
            )}
          </div>
        )}
      </div>

      {/* --- Notifications --- */}
      <div className="id-card mb-6" data-testid="account-notifications-card">
        <NotificationSettings />
      </div>

      {/* --- Sign out --- */}
      <div className="id-card" data-testid="account-signout-card">
        <div className="id-label mb-3 flex items-center gap-2"><ShieldCheck size={12} weight="fill" /> Session</div>
        <button
          onClick={handleLogout}
          className="id-btn-secondary text-sm"
          data-testid="account-signout-btn"
        >
          <SignOut size={14} weight="bold" /> Sign out
        </button>
      </div>

      <CancelSubscriptionModal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        onCancelled={handleCancelled}
      />
    </div>
  );
}
